import type {
	IAnnotation,
	ModelViewerCancelEditingAnnotationCallback,
	ModelViewerSetAnnotationsCallback,
	ModelViewerSetEditingAnnotationCallback,
	ModelViewerSetUpdateAnnotationPositionCallback,
	Viewport,
} from "@centricsoftware/model-viewer";
import { PinColor, PinData, PinPosition } from "csi/model/comment";
import { CommentDetail } from "csi/state/comments";
import { cnl } from "csi/typings";
import { getTrimmedInnerText } from "csi/utility/htmlUtility";
import { ReactionStore } from "csi/utility/mobxUtility";
import { black } from "designSystem/styles/color";
import { action, makeObservable, observable } from "mobx";
import CanvasStore from "./canvasStore";

export default class AnnotationsStore extends ReactionStore { 
    /** Stores all comments that have an annotation */
    @observable.ref private _commentsDetails: CommentDetail[];

    /** reference to Canvas Store */
    @observable.ref private _canvasStore: CanvasStore;

    /** stores whether or not a pin is clickable */
    @observable private _bPinClickable: boolean;

    /** stores comments pin position in Vector3 format */
    @observable private _pinPosition: PinPosition;

    /** stores editable mode for pins*/
    @observable private _pinIsMoving: boolean = false;

    /** Entry point for ModelViewer setAnnotations */
    @observable.ref modelViewerSetAnnotations: ModelViewerSetAnnotationsCallback;

    /** Entry point for ModelViewer setEditingAnnotation */
	@observable.ref modelViewerSetEditingAnnotation: ModelViewerSetEditingAnnotationCallback;

    /** Entry point for ModelViewer setUpdatAnnotationPosition */
    @observable.ref modelViewerSetUpdateAnnotationPosition: ModelViewerSetUpdateAnnotationPositionCallback;

    /** Entry point for ModelViewer cancelEditingAnnotation */
    @observable.ref modelViewerCancelEditingAnnotation: ModelViewerCancelEditingAnnotationCallback;

	/** Entry point for ModelViewer maxDimension utility */
	@observable.ref modelViewerMaxDimension: (object) => number;

	/** Entry point for ModelViewer getBoundingBox utility */
	@observable.ref modelViewerGetBoundingBoxSize: (object) => { x: number; y: number; z: number };

    /** Entry point for ModelViewer viewport data - used in resetting annotation position data */
    @observable.ref modelViewerViewport: Viewport;

    /** Old scale factor of the viewable model */
    @observable private _oldScaleFactor: number | undefined = undefined;

    /** New scale factor of the viewable model */
    @observable private _newScaleFactor: number | undefined = undefined;

    /** Stores reset flag for setting pin back to last position before the move*/
    private _bResetPin: boolean = false;

    /** extension point for corresponding comments selection on pin click */
    onPinClick: (commentUrl: cnl) => void;

    /** extension point for corresponding comments selection on pin hover */
    onPinHover: (commentUrl: cnl) => void;

    /** add pin on 3D model on button click */
    addPin = (newPin: PinData): void => {
    	this.setPinIsMoving(true);
    	this.modelViewerSetEditingAnnotation({
    		active: false,
    		annotationId: "",
    		position: [newPin.position.x, newPin.position.y, newPin.position.z],
    		color: newPin.color,
    		content: "",
    	});
    };

    /** Set a given annotation active property */
    setAnnotationActive = (annotationUrl: cnl): void => {
    	this.setAnnotationPins(this.commentsDetails, null, annotationUrl);
    };

    /** find a comment URL from a given annotation */
    findCommentUrlFromAnnotation = (annotation: IAnnotation): cnl => {
    	return this.commentsDetails.find((comment) => comment.pinName === annotation.annotationId)?.url;
    };

    /** removes current pin from model */
    removePin = (newPin?: PinData): void => {
    	if (newPin) {
    		const removedPins = this._commentsDetails.filter((pin) => pin.pinName !== newPin.count);
    		this.setAnnotationPins(removedPins);
    	}
    };

    /** Update the a pin color of the editing pin */
    updateEditingPinColor = (pin: PinData, color: PinColor): void => {
    	if (!pin) {
    		return;
    	}
    	const existingPin = this._commentsDetails.find((comment) => comment.pinName === pin.count);
    	if (existingPin) {
    		const updatedPins = this._commentsDetails.map((comment) => {
    			if (comment.pinName === pin.count) {
    				comment.threeDInfo = JSON.stringify({
    					...JSON.parse(comment.threeDInfo),
    					color,
    				});
    			}
    			return comment;
    		});
    		this.setAnnotationPins(updatedPins, existingPin.url);
    	} 
    	else {
    		// Go straight to adding the pin in ModelViewer if the pin it doesn't exist (i.e. it's a new pin being added to the model, or the pin was removed and is being re-added to the model during a 'move pin' operation)
    		const currentAnnotations = this.extractAnnotationsFromComments(this.commentsDetails);
    		this.modelViewerSetAnnotations?.([
    			...currentAnnotations,
    			{
    				active: false,
    				annotationId: pin.count,
    				color: color || pin.color,
    				opacity: 1,
    				content: "",
    				title: "",
    				position: [pin.position.x, pin.position.y, pin.position.z],
    				titleStyles: {
    					fontWeight: "bold",
    				},
    				annotationStyles: {
    					width: "125px",
    					top: "-9px",
    					left: "10px",
    					background: black[400],
    					wordWrap: "break-word",
    					overflowWrap: "break-word",
    				},
    			},
    		]);
    	}
    };

    /** sets the reset flag for reseting pin to initial position */
    setResetPin = (val: boolean): void => {
    	this._bResetPin = val;
    };

    /** exits annotation pin edit mode*/
    exitPinEditMode = (): void => {
    	this.setPinIsMoving(false);

    	if (!this.resetPin) {
    		this.modelViewerCancelEditingAnnotation?.();
    	}
    };
    
    /** returns is a pin can reset */
    get resetPin(): boolean {
    	return this._bResetPin;
    };

    /** indicates if pins are clickable or not */
    get bPinClickable(): boolean {
    	return this._bPinClickable;
    }

    /** comments containing pins to show in viewer */
    get commentsDetails(): CommentDetail[] {
    	return this._commentsDetails;
    }

    /** returns back comments pin position */
    get pinPosition(): PinPosition {
    	return this._pinPosition;
    }

    /** returns back if pin is in edit mode */
    get pinIsMoving(): boolean {
    	return this._pinIsMoving;
    }

    /** sets annotation pin position */
    @action setPinPosition = (pos: PinPosition, bReset?: boolean): void => {
    	this.setResetPin(bReset);
    	this._pinPosition = pos;
    };

    /** sets the flag to show if pins are clickable or not */
    @action setPinClickable = (val: boolean): void => {
    	this._bPinClickable = val;
    };

    /** sets annotation pin to editable mode */
    @action setPinIsMoving = (val: boolean): void => {
    	this._pinIsMoving = val;
    };

    /** sets annotation pins on model*/
    @action setAnnotationPins = (commentsDetails: CommentDetail[], selectedCommentUrl?: cnl, activeCommentUrl?: cnl): void => {
    	this._commentsDetails = commentsDetails; 
    	this.modelViewerSetAnnotations?.(this.extractAnnotationsFromComments(commentsDetails, selectedCommentUrl, activeCommentUrl));
    };

    /** Extract annotations from comments and return an empty array if any comment is not of version 2 */
    private extractAnnotationsFromComments = (comments: CommentDetail[], selectedCommentUrl?: cnl, activeCommentUrl?: cnl): IAnnotation[] => {
    	const annotations: IAnnotation[] = [];
    	comments?.forEach((comment, i) => {
    		const { threeDInfo, content, pinName, subject } = comment;
    		const { color, position: { x, y, z }, version } = JSON.parse(threeDInfo);
    		
    		let position = [x, y, z];
    		const title = subject ? `${getTrimmedInnerText(subject)}: ` : null;
    		const active = activeCommentUrl ? activeCommentUrl === comment.url : false;
    		const opacity = selectedCommentUrl ?
    			(selectedCommentUrl === comment.url ? 1 : 0.5) :
    			1; 
    		
    		
    		if ( version !== 2 ) {
    			position = this.convertAnnotationPosition(position);
    		}
    		
    		const annotation: IAnnotation = {
    			active,
    			annotationId: pinName,
    			color,
    			opacity,
    			content: getTrimmedInnerText(content),
    			title,
    			position: [
    				position[0],
    				position[1],
    				position[2],
    			],
    			titleStyles: {
    				fontWeight: "bold",
    			},
    			annotationStyles: {
    				width: "125px",
    				top: "-9px",
    				left: "10px",
    				background: black[400],
    				wordWrap: "break-word",
    				overflowWrap: "break-word",
    			},
    		};
    		annotations.push(annotation);
    	});
    	return annotations;
    };
    
    /** Convert annotation pin positions from the previous C8 viewer to coordinates that will map to modelViewer*/
    private convertAnnotationPosition = (pos: number[]): number[] => {
    	const modelData = this._canvasStore.meshData;
    	const viewport = this.modelViewerViewport;
    	if (!viewport || !modelData || !this.modelViewerMaxDimension || !this.modelViewerGetBoundingBoxSize) {
    		return pos;
    	}

    	// Calculate the old scale factor in the previous viewer
    	const maxDimension = this.modelViewerMaxDimension(modelData);
    	this._oldScaleFactor = this._oldScaleFactor || 3.0 / maxDimension;
    	
    	const boxSize = this.modelViewerGetBoundingBoxSize(modelData);
    	const modelSize = Math.max(boxSize.x, boxSize.y);
    
    	this._newScaleFactor = this._newScaleFactor || (Math.min(viewport.width, viewport.height) * 0.5) / modelSize;
    	let scaleDiff = this._newScaleFactor / 1;

    	//if the model was previously scaled in the old viewer, find the difference between the old scaling factor and the new one
    	if (maxDimension > 3.0) {
    		scaleDiff = this._newScaleFactor / this._oldScaleFactor;
    	}

    	// multiply by the scaling factor difference
    	return pos.map((coord) => coord * scaleDiff);
    };

    constructor(canvasStore: CanvasStore) {
    	super();
    	makeObservable(this);
    	this._canvasStore = canvasStore;
    }
}